// src/components/SimpleFormComponents/SimpleDataOriginForm.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Space, Tooltip } from 'antd';
import { ShopOutlined, UserOutlined } from '@ant-design/icons';
import { useSimpleFormContext } from '../SimpleFormProvider';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import SimpleRadioButtons from '../SimpleRadioButtons';
import SimpleSegment from '../SimpleSegment';
import SimpleEntityProfiles from '../SimpleEntityProfiles';
import SimpleCountry from '../SimpleCountry';
import useFormElement from 'hooks/useFormElement';
import { ENTITY_PROFILE_OPTIONS } from 'constants/EntityConstants';
import { ROW_GUTTER } from 'constants/ThemeConstant';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const VALID_ENTITY_TYPES = ['person', 'company'];
const DEFAULT_ENTITY_TYPE = 'company';

// Sanitize entityType to ensure it's always a valid value
const sanitizeEntityType = value =>
  VALID_ENTITY_TYPES.includes(value) ? value : DEFAULT_ENTITY_TYPE;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleDataOriginForm = ({
  isEntityProfile = false,
  subdocumentField,
  selectable = true,
  dataOrigin, // 'existing_data' | 'new_data'
  existingDataLabel = 'existing_data',
  newDataLabel = 'new_data',
  entityTypeModel = 'manual', // 'auto' | 'manual' | 'company' | 'person'
  entityType: entityTypeProp,
  setEntityType, // kept for backward-compat if someone uses it directly

  // from main form (SimpleFormProvider)
  setState,

  label = 'entity_type',
  showEntityTypeLabel = true, // show/hide label on entity type segment
  showCountryLabel = true, // show/hide label on entity type segment

  // Visibility control props
  hideDataOriginSelector = false, // hide the existing_data/new_data selector
  hideEntityTypeSelector = false, // hide the company/person selector
  hideEntityProfileSelector = true, // hide the entityProfile multi-select (default hidden)
  hideFormCountrySelector = false, // hide the country selector

  // Entity profile options
  entityProfileOptions = ENTITY_PROFILE_OPTIONS.filter(x => x.basic), // options for entityProfile select
  defaultEntityProfiles = ['customer'], // default selected profiles

  // Country config
  locale = appService.getLocale(),

  // Props that should NOT be passed to DOM elements (destructure to prevent spreading)
  showAdditionalInfoForm,
  unique,
  searchByApi,
  searchCnpjByApi,
  searchCpfByApi,
  hideAddressCountrySelector,
  companyPhoneProfile,
  showAddressForm,
  phoneProfile,
  countryCode: countryCodeProp,
  subscriptionCountryCode,
  userCountryCode,
  state,
}) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { form, data, goFocus, setFormCnpjState, setFormCpfState, initFocus } =
    useSimpleFormContext();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const { singleFieldName: dataOriginSingleField, fullFieldName: dataOriginFullField } =
    useFormElement('dataOrigin', subdocumentField);
  const { singleFieldName: entityTypeSingleField, fullFieldName: entityTypeFullField } =
    useFormElement('entityType', subdocumentField);
  const { singleFieldName: entityProfileSingleField, fullFieldName: entityProfileFullField } =
    useFormElement('entityProfile', subdocumentField);

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  // Sanitize entityType: ensure it's always 'person' or 'company', defaulting to 'company'
  // For 'manual' or 'auto' entityTypeModel, use the prop or default to 'company'
  const entityType = sanitizeEntityType(
    entityTypeProp ??
      (VALID_ENTITY_TYPES.includes(entityTypeModel) ? entityTypeModel : DEFAULT_ENTITY_TYPE)
  );

  // Lock entityType and country selectors if entity is persisted AND was enriched via external API
  // This prevents changing the entity type/country of an enriched entity (which would invalidate the data)
  const isEnrichedAndPersisted = data?._id && data?.dataEnrichment?.isEnriched === true;

  // NOTE: Removed Form.useWatch('countryCode') - it was causing issues by
  // picking up search text during typing. Country sync now only via selection.

  // Precedence: subscription → user → data → locale → appService
  const getInitialCountryCode = () => {
    return (
      (subscriptionCountryCode ? String(subscriptionCountryCode).toUpperCase() : null) ||
      (userCountryCode ? String(userCountryCode).toUpperCase() : null) ||
      (data?.countryCode ? String(data.countryCode).toUpperCase() : null) ||
      (locale ? String(locale).toUpperCase() : null) ||
      String(appService.getLocale()).toUpperCase()
    );
  };

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [countryCode, setCountryCode] = useState(() => getInitialCountryCode());
  // Local state for segment to enable instant visual feedback without waiting for parent re-render
  const [localEntityType, setLocalEntityType] = useState(entityType);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (entityType && !form.getFieldValue(entityTypeFullField)) {
      form.setFieldValue(entityTypeFullField, entityType);
    }
    if (dataOrigin && !form.getFieldValue(dataOriginFullField)) {
      form.setFieldValue(dataOriginFullField, dataOrigin);
    }
    // Initialize entityProfile with default values if not set - must always be an array for mode="multiple"
    const currentProfile = form.getFieldValue(entityProfileFullField);
    if (!Array.isArray(currentProfile)) {
      form.setFieldValue(entityProfileFullField, defaultEntityProfiles || []);
    }
  }, [
    form,
    entityType,
    entityTypeFullField,
    dataOrigin,
    dataOriginFullField,
    entityProfileFullField,
    defaultEntityProfiles,
  ]);

  // NOTE: Removed Form.useWatch sync for countryCode because it was picking up
  // search text during typing in the Select. The sync now happens only via:
  // 1. handleCountryChange (when user SELECTS a country)
  // 2. data?.countryCode useEffect (when loading from database)

  // Sync local entityType with prop when it changes externally (e.g., data load)
  useEffect(() => {
    if (entityType && entityType !== localEntityType) {
      setLocalEntityType(entityType);
      // Also sync the form field value to ensure the Segment displays correctly
      form?.setFieldValue(entityTypeFullField, entityType);
    }
  }, [entityType, form, entityTypeFullField]);

  // Update when data changes (API load) OR subscription/user changes (new document)
  useEffect(() => {
    if (data?.countryCode) {
      const newCode = data.countryCode.toUpperCase();
      setCountryCode(newCode);
      setState?.(prev => ({ ...prev, countryCode: newCode }));
    } else if (!data?._id) {
      // For new documents (no _id), use subscription or user country
      const newCode = (
        (subscriptionCountryCode ? String(subscriptionCountryCode).toUpperCase() : null) ||
        (userCountryCode ? String(userCountryCode).toUpperCase() : null)
      );
      if (newCode) {
        setCountryCode(newCode);
        setState?.(prev => ({ ...prev, countryCode: newCode }));
      }
    }
  }, [data?.countryCode, subscriptionCountryCode, userCountryCode, data?._id, setState]);

  // Initial focus on document field based on entityType and countryCode
  // IMPORTANT: Skip this auto-focus if parent form has initFocus defined
  // This allows the parent form to control the initial focus field
  useEffect(() => {
    // Skip auto-focus if parent form has initFocus defined
    if (initFocus) return;

    // Only focus if isEntityProfile is enabled and we have new_data selected
    if (!isEntityProfile || !goFocus) return;

    const currentDataOrigin = form?.getFieldValue(dataOriginFullField);
    if (currentDataOrigin !== 'new_data') return;

    const initialEntityType = form?.getFieldValue(entityTypeFullField) || entityType;
    const initialCountry = countryCode || getInitialCountryCode();

    // Small delay to ensure form fields are rendered
    const timer = setTimeout(() => {
      const isBrazil = initialCountry === 'BR';
      let fieldToFocus;

      if (isBrazil) {
        fieldToFocus = initialEntityType === 'company' ? 'cnpj.value' : 'cpf.value';
      } else {
        // For non-BR countries, focus on foreignIdentifier (used for international tax/passport)
        fieldToFocus = 'foreignIdentifier';
      }

      goFocus(fieldToFocus);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------

  // Focus on the appropriate document field based on entityType and countryCode
  const focusDocumentField = (type, country, delay = 100) => {
    if (!goFocus) return;

    const isBrazil = country === 'BR';
    let fieldToFocus;

    if (isBrazil) {
      fieldToFocus = type === 'company' ? 'cnpj.value' : 'cpf.value';
    } else {
      // For non-BR countries, focus on foreignIdentifier (used for international tax/passport)
      fieldToFocus = 'foreignIdentifier';
    }

    setTimeout(() => goFocus(fieldToFocus), delay);
  };

  // Helper function to clear ALL uniqueKey fields (CPF, CNPJ, foreignIdentifier)
  // This ensures only ONE uniqueKey is saved to the database
  const clearAllUniqueKeyFields = () => {
    form?.setFieldValue(['cpf', 'value'], undefined);
    form?.setFieldValue(['cnpj', 'value'], undefined);
    form?.setFieldValue('foreignIdentifier', undefined);
    setFormCpfState?.('cpf.value', { lastSearchFound: null });
    setFormCnpjState?.('cnpj.value', { lastSearchFound: null });
  };

  const handleEntityTypeChange = useCallback(
    value => {
      // 1. Update local state immediately - segment animates instantly
      setLocalEntityType(value);
      setEntityType?.(value);

      // 2. Defer heavy parent state update to next frame after paint
      requestAnimationFrame(() => {
        setState?.(prev => ({ ...prev, entityType: value }));
        clearAllUniqueKeyFields();
        focusDocumentField(value, countryCode);
      });
    },
    [countryCode, setEntityType, setState]
  );

  const handleCountryChange = value => {
    const newCountryCode = value?.toUpperCase() || 'BR';

    // Validate: must be a 2-letter ISO country code
    if (!newCountryCode || newCountryCode.length !== 2) {
      return;
    }

    setCountryCode(newCountryCode);
    setState?.(prev => ({ ...prev, countryCode: newCountryCode }));

    // IMPORTANT: Clear ALL uniqueKey fields when country changes
    // This prevents saving multiple documents (CPF, CNPJ, foreignIdentifier) to the database
    // Only one uniqueKey should exist based on: BR+COMPANY=CNPJ, BR+PERSON=CPF, Other=foreignIdentifier
    // Always clear on change - this handler is only called when user actively changes the country
    clearAllUniqueKeyFields();

    // Update phone countryCode only if phone.value is empty
    const phoneFieldValue = form?.getFieldValue(['phone', 'value']);
    const actualPhoneValue =
      phoneFieldValue && typeof phoneFieldValue === 'object'
        ? phoneFieldValue.value
        : phoneFieldValue;
    const phoneIsEmpty = !actualPhoneValue || String(actualPhoneValue).trim() === '';

    if (phoneIsEmpty) {
      form?.setFieldValue(['phone', 'countryCode'], newCountryCode);
    }

    // Update address countryCode only if address fields are empty (zip/address1)
    const zipCode = form?.getFieldValue(['address', 'zip']);
    const address1 = form?.getFieldValue(['address', 'address1']);
    const addressIsEmpty =
      (!zipCode || String(zipCode).trim() === '') && (!address1 || String(address1).trim() === '');
    if (addressIsEmpty) {
      form?.setFieldValue(['address', 'countryCode'], newCountryCode);
    }

    // Focus on appropriate document field after country change
    const currentEntityType = form?.getFieldValue(entityTypeFullField) || entityType;
    focusDocumentField(currentEntityType, newCountryCode);
  };

  const handleDataOriginChange = ev => {
    setState?.(prev => ({ ...prev, dataOrigin: ev.target.value }));
  };

  const handleEntityProfileChange = value => {
    setState?.(prev => ({ ...prev, entityProfile: value }));
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  // Hide entire component if all selectors are hidden
  const showDataOrigin = selectable && !hideDataOriginSelector;
  const showEntityType =
    selectable &&
    !hideEntityTypeSelector &&
    isEntityProfile &&
    entityTypeModel === 'manual';
  const showEntityProfile = selectable && !hideEntityProfileSelector && isEntityProfile;
  const showCountrySelector = !hideFormCountrySelector;

  // Calculate column widths based on visible elements
  // entityType: 8 (when all 3) or 10 (when no country)
  // entityProfile: 10 (when all 3) or 14 (when no country) or 18 (when no entityType)
  // country: 6
  const entityTypeCol = showCountrySelector ? 8 : 10;
  const entityProfileCol = showEntityType
    ? showCountrySelector
      ? 10
      : 14
    : showCountrySelector
      ? 18
      : 24;

  // If nothing to show, return null
  if (!showDataOrigin && !showEntityType && !showEntityProfile && !showCountrySelector) return null;

  return (
    <Card className="fit-components">
      <Space orientation="vertical" size={16} style={{ width: '100%' }} className="mb-4">
        {(showEntityType || showEntityProfile || showCountrySelector) && (
          <Row gutter={ROW_GUTTER}>
            <Col xs={24} sm={entityTypeCol}>
              {showEntityType && (
                <SimpleSegment
                  name={entityTypeSingleField}
                  label={showEntityTypeLabel ? label : undefined}
                  noLabel={!showEntityTypeLabel}
                  value={localEntityType}
                  disabled={isEnrichedAndPersisted}
                  options={[
                    {
                      label: (
                        <Tooltip title={translateX('legal_person')}>
                          <span>{translateX('company')}</span>
                        </Tooltip>
                      ),
                      value: 'company',
                      icon: <ShopOutlined />,
                    },
                    {
                      label: (
                        <Tooltip title={translateX('individual_person')}>
                          <span>{translateX('person')}</span>
                        </Tooltip>
                      ),
                      value: 'person',
                      icon: <UserOutlined />,
                    },
                  ]}
                  block
                  subdocumentField={subdocumentField}
                  onChange={handleEntityTypeChange}
                />
              )}
            </Col>

            <Col xs={24} sm={entityProfileCol}>
              {showEntityProfile && (
                <SimpleEntityProfiles
                  name={entityProfileSingleField}
                  subdocumentField={subdocumentField}
                  options={entityProfileOptions}
                  onChange={handleEntityProfileChange}
                />
              )}
            </Col>

            <Col xs={24} sm={6}>
              {showCountrySelector && (
                <Tooltip title={translateX('country_of_registration')}>
                  <div>
                    <SimpleCountry
                      name="countryCode"
                      subdocumentField={subdocumentField}
                      label="country"
                      noLabel={!showCountryLabel}
                      countryCode={locale}
                      subscriptionCountryCode={subscriptionCountryCode}
                      userCountryCode={userCountryCode}
                      onCountryChange={handleCountryChange}
                      allowClear={false}
                      disabled={isEnrichedAndPersisted}
                    />
                  </div>
                </Tooltip>
              )}
            </Col>
          </Row>
        )}
      </Space>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleDataOriginForm;

// *******************************************************************************
// HELPERS
// *******************************************************************************
